import React from 'react';
import { format, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear, subYears } from 'date-fns';
import { ChevronDown, Calendar } from 'lucide-react';
import clsx from 'clsx';

export type DateRangePreset = 'this-month' | 'last-month' | 'last-3-months' | 'last-6-months' | 'last-9-months' | 'this-year' | 'last-year' | 'custom';

export interface DateRange {
  from: string; // ISO date string (YYYY-MM-DD)
  to: string; // ISO date string (YYYY-MM-DD)
}

interface DateRangeSelectorProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  className?: string;
}

function formatDateIso(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

function getPresetRange(preset: DateRangePreset): DateRange {
  const now = new Date();
  const lastMonthEnd = endOfMonth(subMonths(now, 1));
  
  switch (preset) {
    case 'this-month':
      return {
        from: formatDateIso(startOfMonth(now)),
        to: formatDateIso(now),
      };
    case 'last-month':
      const lastMonthStart = startOfMonth(subMonths(now, 1));
      return {
        from: formatDateIso(lastMonthStart),
        to: formatDateIso(lastMonthEnd),
      };
    case 'last-3-months':
      return {
        from: formatDateIso(startOfMonth(subMonths(now, 3))),
        to: formatDateIso(lastMonthEnd),
      };
    case 'last-6-months':
      return {
        from: formatDateIso(startOfMonth(subMonths(now, 6))),
        to: formatDateIso(lastMonthEnd),
      };
    case 'last-9-months':
      return {
        from: formatDateIso(startOfMonth(subMonths(now, 9))),
        to: formatDateIso(lastMonthEnd),
      };
    case 'this-year':
      return {
        from: formatDateIso(startOfYear(now)),
        to: formatDateIso(endOfYear(now)),
      };
    case 'last-year':
      const lastYear = subYears(now, 1);
      return {
        from: formatDateIso(startOfYear(lastYear)),
        to: formatDateIso(endOfYear(lastYear)),
      };
    case 'custom':
      // Return last month as default for custom
      return {
        from: formatDateIso(startOfMonth(subMonths(now, 1))),
        to: formatDateIso(lastMonthEnd),
      };
  }
}

export function detectPreset(range: DateRange | undefined | null): DateRangePreset {
  if (!range || !range.from || !range.to) {
    return 'custom';
  }

  const thisMonth = getPresetRange('this-month');
  const lastMonth = getPresetRange('last-month');
  const last3Months = getPresetRange('last-3-months');
  const last6Months = getPresetRange('last-6-months');
  const last9Months = getPresetRange('last-9-months');
  const thisYear = getPresetRange('this-year');
  const lastYear = getPresetRange('last-year');

  if (range.from === thisMonth.from && range.to === thisMonth.to) {
    return 'this-month';
  }
  if (range.from === lastMonth.from && range.to === lastMonth.to) {
    return 'last-month';
  }
  if (range.from === last3Months.from && range.to === last3Months.to) {
    return 'last-3-months';
  }
  if (range.from === last6Months.from && range.to === last6Months.to) {
    return 'last-6-months';
  }
  if (range.from === last9Months.from && range.to === last9Months.to) {
    return 'last-9-months';
  }
  if (range.from === thisYear.from && range.to === thisYear.to) {
    return 'this-year';
  }
  if (range.from === lastYear.from && range.to === lastYear.to) {
    return 'last-year';
  }
  return 'custom';
}

export const DATE_RANGE_PRESET_OPTIONS: Array<{ value: DateRangePreset; label: string }> = [
  { value: 'this-month', label: 'This month' },
  { value: 'last-month', label: 'Last month' },
  { value: 'last-3-months', label: 'Last 3 months' },
  { value: 'last-6-months', label: 'Last 6 months' },
  { value: 'last-9-months', label: 'Last 9 months' },
  { value: 'this-year', label: 'This Year' },
  { value: 'last-year', label: 'Last Year' },
  { value: 'custom', label: 'Custom Date Range' },
];

export function getPresetLabel(range: DateRange | undefined | null): string {
  if (!range || !range.from || !range.to) {
    return 'Custom Date Range';
  }
  const preset = detectPreset(range);
  const match = DATE_RANGE_PRESET_OPTIONS.find((option) => option.value === preset);
  return match?.label ?? 'Custom Date Range';
}

function formatDateRangeDisplay(from: string, to: string): string {
  try {
    const fromDate = new Date(from);
    const toDate = new Date(to);
    
    // Format: "Jan 1 - Dec 31, 2024" or "Jan 1 - Jan 31, 2024" (same year)
    const fromFormatted = format(fromDate, 'MMM d');
    const toFormatted = format(toDate, 'MMM d');
    const year = format(toDate, 'yyyy');
    
    // Check if same year
    const fromYear = format(fromDate, 'yyyy');
    if (fromYear === year) {
      // Same year: "Jan 1 - Dec 31, 2024"
      return `${fromFormatted} - ${toFormatted}, ${year}`;
    } else {
      // Different years: "Dec 1, 2023 - Jan 31, 2024"
      return `${format(fromDate, 'MMM d, yyyy')} - ${format(toDate, 'MMM d, yyyy')}`;
    }
  } catch {
    return 'Invalid date range';
  }
}

export function DateRangeSelector({ value, onChange, className }: DateRangeSelectorProps) {
  // Ensure value is always valid - default to last month
  const validValue = React.useMemo(() => {
    if (!value || !value.from || !value.to) {
      return getPresetRange('last-month');
    }
    return value;
  }, [value]);

  const [preset, setPreset] = React.useState<DateRangePreset>(() => detectPreset(validValue));
  const [isOpen, setIsOpen] = React.useState(false);
  const [customFrom, setCustomFrom] = React.useState(validValue.from);
  const [customTo, setCustomTo] = React.useState(validValue.to);
  
  // Track when we're manually changing the preset to prevent useEffect from overwriting it
  const isManuallyChangingPreset = React.useRef(false);

  // Initialize with default dates if value is empty
  React.useEffect(() => {
    if (!value || !value.from || !value.to) {
      const defaultRange = getPresetRange('last-month');
      onChange(defaultRange);
    }
  }, []); // Only run on mount

  React.useEffect(() => {
    // Skip detection if we're in the middle of a manual preset change
    if (isManuallyChangingPreset.current) {
      return;
    }
    
    const detected = detectPreset(validValue);
    setPreset(detected);
    if (detected === 'custom') {
      setCustomFrom(validValue.from);
      setCustomTo(validValue.to);
    }
  }, [validValue, preset]);

  const handlePresetChange = (newPreset: DateRangePreset) => {
    // Mark that we're manually changing the preset
    isManuallyChangingPreset.current = true;
    
    setPreset(newPreset);
    if (newPreset !== 'custom') {
      const range = getPresetRange(newPreset);
      onChange(range);
      setIsOpen(false);
      
      // Reset the flag after a short delay to allow the parent to update
      // This prevents the useEffect from overwriting the preset during the update
      setTimeout(() => {
        isManuallyChangingPreset.current = false;
      }, 100);
    } else {
      // For custom, keep the flag set to prevent useEffect from overwriting
      // The flag will be reset when the user actually changes the dates in the custom form
      // This ensures the custom form stays visible
    }
  };


  const presetOptions = DATE_RANGE_PRESET_OPTIONS;

  // Approach 1: Show actual dates for custom ranges
  // Approach 2: Show dates for all presets in parentheses
  // Approach 3: Two-line display with preset name and dates
  // Reserved for future use - displayLabel calculation
  // const displayLabel = React.useMemo(() => {
  //   const presetOption = presetOptions.find(opt => opt.value === preset);
  //   const baseLabel = presetOption?.label || 'Custom Date Range';
  //   const dateRange = formatDateRangeDisplay(validValue.from, validValue.to);
  //   
  //   // For custom ranges, always show the actual dates (Approach 1)
  //   if (preset === 'custom') {
  //     return dateRange;
  //   }
  //   
  //   // Show dates for all presets: "Last month (Dec 1 - Dec 31, 2024)" (Approach 2)
  //   return `${baseLabel} (${dateRange})`;
  // }, [preset, validValue, presetOptions]);

  return (
    <div className={clsx('relative', className)}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 h-11 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
        aria-label="Select date range"
        aria-expanded={isOpen}
      >
        <Calendar className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        {/* Show only the date range when closed */}
        <span className="whitespace-nowrap">
          {formatDateRangeDisplay(validValue.from, validValue.to)}
        </span>
        <ChevronDown className={clsx('h-4 w-4 transition-transform flex-shrink-0', isOpen && 'rotate-180')} aria-hidden="true" />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute left-0 mt-2 w-72 z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl overflow-hidden">
            <div className="p-4 space-y-4">
              <div className="space-y-2">
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2 text-left">
                  Quick Select
                </label>
                <div className="space-y-1">
                  {presetOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handlePresetChange(option.value)}
                      className={clsx(
                        'w-full text-left px-3 py-2.5 text-sm rounded-md transition-colors whitespace-nowrap flex items-center',
                        preset === option.value
                          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      )}
                    >
                      <span className="flex-1 text-left">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {preset === 'custom' && (
                <div 
                  className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-3"
                  aria-label="Custom Date Range"
                >
                  <div className="space-y-3">
                    <div>
                      <label htmlFor="custom-from" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5 text-left">
                        From
                      </label>
                      <input
                        id="custom-from"
                        type="date"
                        value={customFrom}
                        onChange={(e) => {
                          setCustomFrom(e.target.value);
                          // Reset the flag when user starts editing custom dates
                          isManuallyChangingPreset.current = false;
                          if (e.target.value && customTo && e.target.value <= customTo) {
                            onChange({ from: e.target.value, to: customTo });
                          }
                        }}
                        max={customTo || undefined}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-left"
                      />
                    </div>
                    <div>
                      <label htmlFor="custom-to" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5 text-left">
                        To
                      </label>
                      <input
                        id="custom-to"
                        type="date"
                        value={customTo}
                        onChange={(e) => {
                          setCustomTo(e.target.value);
                          // Reset the flag when user starts editing custom dates
                          isManuallyChangingPreset.current = false;
                          if (customFrom && e.target.value && customFrom <= e.target.value) {
                            onChange({ from: customFrom, to: e.target.value });
                          }
                        }}
                        min={customFrom || undefined}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-left"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

